import type {
  ReleaseProfileFormula,
  ReleaseProfileFormulaItem,
  ReleaseProfilesAndVariables,
} from '../../types/release-profiles.ts';
import type { EmissionsComplianceMetricConfig } from '../../types/emissions.ts';
import { VariableNameFinder } from '../formulas/variable-name-finder.ts';
import { formulaPredefinedVariables, variablesImplyProduct } from '../formulas/formula-utils.ts';
import { UnitsTracker } from '../formulas/units.ts';

function complianceMetricReferencedVariableNames(formulaObj: ReleaseProfileFormula): Set<string> {
  const formulasByVariableName = new Map(
    Object.values(formulaObj.formulas).map((formula) => [formula.nameForVariable, formula]),
  );
  const names = new Set<string>();
  const visit = (formula: ReleaseProfileFormulaItem) => {
    const found = VariableNameFinder.findInString(formula.formula);
    if (found.status === 'error') return;
    for (const name of found.variableNames) {
      if (names.has(name)) continue;
      names.add(name);
      const internalFormula = formulasByVariableName.get(name);
      if (internalFormula) visit(internalFormula);
    }
  };
  for (const formulaItem of Object.values(formulaObj.formulas)) {
    visit(formulaItem);
  }
  return names;
}

export function complianceMetricMixCalculationVariableNames(
  formulaObj: ReleaseProfileFormula,
  profiles: Pick<ReleaseProfilesAndVariables, 'mixCalculations'>,
): Set<string> {
  const referencedVariableNames = complianceMetricReferencedVariableNames(formulaObj);
  return new Set(
    profiles.mixCalculations.map((mix) => mix.nameForVariable).filter((name) => referencedVariableNames.has(name)),
  );
}

/** Validates that a metric references only values its emissions evaluator can provide. */
export function complianceMetricDependencyValidationError(
  formulaObj: ReleaseProfileFormula,
  profiles: Pick<ReleaseProfilesAndVariables, 'constants' | 'questions' | 'chemicalProfiles' | 'mixCalculations'>,
): string | null {
  const mixCalculationVariableNames = complianceMetricMixCalculationVariableNames(formulaObj, profiles);
  const referencedMixCalculation = profiles.mixCalculations.find((mix) =>
    mixCalculationVariableNames.has(mix.nameForVariable),
  );
  if (referencedMixCalculation) {
    return `Compliance metrics cannot use mix calculation "${referencedMixCalculation.nameFriendly}".`;
  }
  const validChoiceLists = formulaObj.choiceLists;
  for (const formula of Object.values(formulaObj.formulas)) {
    for (const output of Object.values(formula.outputs)) {
      for (const condition of output.ifChoices) {
        const choiceList = validChoiceLists[condition.choiceListID];
        if (!choiceList?.choices.some((choice) => choice.choiceID === condition.choiceID)) {
          return 'A compliance metric condition references a missing question or answer.';
        }
      }
    }
    if (formula.formula.trim() === '') continue;
    const found = VariableNameFinder.findInString(formula.formula);
    if (found.status === 'error') return found.error;
    const supportedVariableNames = new Set([
      ...formulaPredefinedVariables.map((variable) => variable.nameForVariable),
      ...profiles.constants.map((variable) => variable.nameForVariable),
      ...profiles.questions.map((variable) => variable.nameForVariable),
      ...profiles.chemicalProfiles.map((variable) => variable.nameForVariable),
      ...Object.values(formulaObj.formulas).map((candidate) => candidate.nameForVariable),
    ]);
    const unsupported = Array.from(found.variableNames).find((name) => !supportedVariableNames.has(name));
    if (unsupported) return `Compliance metrics cannot use the unsupported variable "${unsupported}".`;
  }
  return null;
}

/**
 * Validates the comparison unit entered on a metric-based limit, which is free text rather than a choice from a list
 * of convertible units: that list is expensive to build and can never be complete, because a unit has several
 * spellings ('lb' and 'lbs' name the same unit) and unit products multiply out combinatorially. Returns `null` when
 * the entry is usable, and an explanation to show the user otherwise.
 */
export function complianceMetricLimitUnitError(
  limitUnit: string,
  config: EmissionsComplianceMetricConfig | null,
): string | null {
  if (!config) {
    return null;
  }

  const entered = limitUnit.trim();
  if (entered === '') {
    return 'Enter a comparison unit.';
  }

  const enteredUnits = UnitsTracker.fromParsedUnitString(entered);
  if (enteredUnits.isUnknown()) {
    return `"${entered}" is not a unit this system understands.`;
  }

  // A metric that names no expected unit gives us nothing to check the entry against, so any unit we understand is
  // accepted; the compliance evaluation skips formula outputs it cannot convert to the limit's unit.
  const expected = config.expectedUnits?.value.trim();
  if (!expected) {
    return null;
  }

  const expectedUnits = UnitsTracker.fromParsedUnitString(expected);
  if (expectedUnits.isUnknown()) {
    return `This compliance metric expects units of "${expected}", which this system does not understand.`;
  }

  if (typeof expectedUnits.convertTo(enteredUnits, 1) !== 'number') {
    return `This compliance metric produces values in ${expectedUnits.toString()}, which cannot be converted to ${enteredUnits.toString()}.`;
  }

  return null;
}

/** Product-dependent metrics require product assignments on every selected source. */
export function complianceMetricRequiresProducts(formulaObj: ReleaseProfileFormula): boolean {
  return variablesImplyProduct(complianceMetricReferencedVariableNames(formulaObj));
}
